import { useContext, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import * as route from 'route';
import {
    DetailsAppShell,
    useToasts,
    RoundButton,
    Content,
    EditorHelper,
    EditorBase
} from 'components'
import { Formik } from 'formik';
import { PCSContext } from 'Router/PCSContext';

import * as Model from './model';
import Editor from './Editor';

export interface FromRouteParams {
    itemId: number;
}

type DTOModel = Model.TrainingSetDTO
type TDataModel = DTOModel
type TQueryModel = Model.TrainingSetFindQueryDTO
type TResultModel = Model.TrainingSetUpdateResultDTO
const ContractAPI = Model.API.ITrainingSetContract


export default function EditorPage() {
    const { itemId } = useParams<{ itemId: string }>();
    const history = useHistory();
    const feedback = useToasts();
    const pcsContext = useContext(PCSContext)

    const [state, setState] = useState<{
        item: TDataModel
    } | undefined>();

    const [blockButtons, setBlockButtons] = useState(false);

    const itemIdParsed = parseInt(itemId, 10)

    const settings: DetailsPageSettings<TDataModel, TQueryModel> = {
        contract: ContractAPI,
        pageSettings: {
            title: (item: DTOModel) => item.id > 0 ? item.name : "Define new Training Set",
            subtitle: (item: DTOModel) => "Training sets list",
            details: {
                routeToNew: (itemId: number) => route.managTrainingSetDetailsRoute.createRoute({ itemId })
            },
        },
        newItem: () => ({
            id: 0,
            isActive: true,
            createdOn: new Date().toISOString(),
            editedOn: new Date().toISOString(),
            certificates: [],
            forDivision: [],
            forCountry: [],
            forFunctionalJob: [],
            forFunctionalJobFamily: [],
            forProductGroup: [],
            forUserType: { id: 0, code: "", display: "Any" },
            name: "New name",
            automaticRenewal: false,
            reagisterAllUsers: false,
        }),
        // handlers: {
        //     doUpdate: (row: TDataModel) => {
        //         ContractAPI
        //             .update(row)
        //             .then(r => {
        //                 const result = r as { itemID: string | number }
        //                 showSuccess('Item added ...page will refresh')
        //                 const route = settings.pageSettings.details.routeToNew(result.itemID as number)
        //                 history.replace(route)
        //                 window.location.reload()
        //             })
        //     }
        // }
    }

    function loadDataBase() {
        if (itemIdParsed === 0)
            setState(s => ({ item: settings.newItem() as TDataModel }))
        else
            settings.contract
                .get(itemIdParsed)
                .then(x => setState(s => ({ item: x })))
        
        setBlockButtons(false);
    }
    useEffect(loadDataBase, []);

    const doUpdate = settings.handlers?.doUpdate ?? ((row: TDataModel) => {
        setBlockButtons(true);
        settings.contract
            .update(row)
            .then(e => {
                feedback.showSuccess('Training set updated');
                if ((!!e) && typeof e === "object" && e != null ) {
                    const response = e as {enrollmentResult: TResultModel}
                    // const result = e.enrollmentResult as TResultModel;
                    //@ts-ignore eslint-disable-next-line 
                    // if (typeof e.usersEnrolled === "number" && parseInt(result.usersEnrolled, 10) > 0)
                    //     //@ts-ignore eslint-disable-next-line 
                    //     feedback.showInfo(`Enrolled ${e.usersEnrolled} ${e.usersEnrolled === 1 ? "user" : "users"}`)
                    /////
                    if(response.enrollmentResult !== null){
                        if (response.enrollmentResult.failed.length &&
                            !response.enrollmentResult.success.length) {          
                            feedback.showMany('All enrollments failed', 
                                response.enrollmentResult.failed.map(resp => `User ${resp.userEmail} was not enrolled with message: ${resp.message}`)
                          , true);          
                        }
                        if (!response.enrollmentResult.failed.length && response.enrollmentResult.success.length) {
                            feedback.showInfo(`${response.enrollmentResult.success.length} ${response.enrollmentResult.success.length === 1 ? "enrollment" : "enrollments"} succeeded`)
                        }
                        if (response.enrollmentResult.failed.length && response.enrollmentResult.success.length) {
                            feedback.showInfo(`${response.enrollmentResult.success.length} ${response.enrollmentResult.success.length === 1 ? "enrollment" : "enrollments"} succeeded`)
                            feedback.showMany('Some enrollments failed', 
                                response.enrollmentResult.failed.map(resp => `User ${resp.userEmail} was not enrolled with message: ${resp.message}`)
                          , true);      
                        } 
                        if (!response.enrollmentResult.failed.length && !response.enrollmentResult.success.length) {
                            feedback.showInfo('No enrollments done');
                        } 
                    }
                }
            })
            .then(() => loadDataBase())
            .catch(e => {
                feedback.showError(e.message ? `Cannot update Training Set. Additional message: ${e.message}` : "Cannot update Training Set.");
                setBlockButtons(false);
            })
    })

    function doDelete(row: TDataModel) {
        setBlockButtons(true);
        settings.contract
            .delete(row)
            .then(r => {
                feedback.showSuccess('Training set removed')
                history.goBack()
                setBlockButtons(false);
            })
            .catch(e => feedback.showError(e.message ? `Cannot delete Training Set. Additional message: ${e.message}` : "Cannot delete Training Set."))
    }

    function doAdd(row: TDataModel) {
        setBlockButtons(true);
        settings.contract
            .add(row)
            .then(r => {
                feedback.showSuccess('Training set added ...page will refresh')
                const route = settings.pageSettings.details.routeToNew(r.itemID as number)
                history.replace(route)
                window.location.reload()
            })
            .catch(e => {
                feedback.showError(e.message ? `Cannot add Training Set. Additional message: ${e.message}` : "Cannot add Training Set.");
                setBlockButtons(false);
            })
    }

    const onCommit = (item: TDataModel) => {
        const isNew = (itemIdParsed === 0)
        isNew ? doAdd(item) : doUpdate(item);
    };

    const onDelete = (item: TDataModel) => {
        pcsContext!.showQuestion({
            onConfirmed: () => doDelete(item),
            confirmDescription: "Do you want to delete selected Training Set?"
        })
    }

    function renderActions(helper: EditorHelper<TDataModel>) {
        return (<>

            {helper.values.id == 0 ? <>
                <RoundButton color="secondary" variant="contained"
                    onClick={() => helper.submitForm()}
                    disabled={blockButtons}
                >
                    CREATE TRAINING SET
                </RoundButton>
            </> : <>
                <RoundButton color="primary"
                    onClick={() => onDelete(helper.values)}
                    disabled={blockButtons}
                >
                    DELETE TRAINING SET
                </RoundButton>
                <RoundButton color="secondary" variant="contained"
                    onClick={() => helper.submitForm()}
                    disabled={blockButtons}
                >
                    SAVE CHANGES
                </RoundButton></>
            }
        </>)
    }

    const toEdit = state?.item


    return (<>
        <DetailsAppShell
            title={toEdit ? settings.pageSettings.title(toEdit) : ""}
            subtitle={toEdit ? settings.pageSettings.subtitle(toEdit) : ""}
            loading={!state}
            defaultGoBackPath=".."
            logout={pcsContext?.onLogout}
        >
            <Formik
                initialValues={(toEdit ?? settings.newItem()) as TDataModel}
                key={toEdit?.id}
                onSubmit={values => onCommit(values)}
            >
                {formikProps => {
                    const helper = EditorBase.getHelper<TDataModel>(formikProps, () => { }, onCommit)
                    return <>

                        <Content
                            loading={!state}
                            title={(itemIdParsed === 0) ? "Define new Training set" : "Edit Training set"}
                            renderActions={() => renderActions(helper)}>
                            <Editor helper={helper} />
                        </Content>
                    </>
                }}
            </Formik>
        </DetailsAppShell>
    </>)
}
